// Browser side of the voice agent: WebRTC to OpenAI Realtime via our backend, plus tool calls and transcripts
const API_URL = process.env.REACT_APP_BACKEND_URL;
const norm = (t) => t.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const READ_VERBATIM = 'Read the following text out loud COMPLETELY, word for word, in a natural warm voice. Do not shorten, summarise, skip, reorder or add anything, and do not stop early:';
const DISCLAIMER_SAID = /(backtest|बैकटेस्ट)\s*(report|रिपोर्ट)/i;

export class SupportVoiceClient {
  constructor({ session, onResource, onHandoff, onCaption, onStatus }) {
    this.session = session;
    this.cb = { onResource, onHandoff, onCaption, onStatus };
    this.pending = [];
    this.escalated = false;
    this.shared = [];
  }

  async start() {
    this.cb.onStatus('connecting');
    this.pc = new RTCPeerConnection();
    this.audio = document.createElement('audio');
    this.audio.autoplay = true;
    document.body.appendChild(this.audio);
    this.pc.ontrack = (e) => { this.audio.srcObject = e.streams[0]; };
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.stream.getTracks().forEach((t) => this.pc.addTrack(t, this.stream));
    this.dc = this.pc.createDataChannel('oai-events');
    this.dc.onopen = () => this.configure();
    this.dc.onmessage = (e) => this.handle(JSON.parse(e.data));
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    const res = await fetch(`${API_URL}/api/support/realtime/negotiate?cid=${this.session.conversation_id}`, {
      method: 'POST', headers: { 'Content-Type': 'application/sdp' }, body: offer.sdp,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Voice service unavailable');
    }
    const { sdp, max_seconds: maxSeconds, session, rule_swaps: ruleSwaps } = await res.json();
    this.sessionConfig = session;
    this.instructions = session.instructions;
    this.ruleSwaps = ruleSwaps || {};
    await this.pc.setRemoteDescription({ type: 'answer', sdp });
    this.flushTimer = setInterval(() => this.flush(), 5000);
    this.capTimer = setTimeout(() => this.stop('timeup'), maxSeconds * 1000);
  }

  send(event) {
    if (this.dc?.readyState === 'open') this.dc.send(JSON.stringify(event));
  }

  configure() {
    // OpenAI occasionally starts a call with default settings, so re-apply ours before the greeting
    const { model, ...cfg } = this.sessionConfig || {};
    this.send({ type: 'session.update', session: cfg });
  }

  handle(ev) {
    if (ev.type === 'session.updated' && !this.greeted) {
      this.greeted = true;
      this.send({ type: 'response.create' });
      this.cb.onStatus('live');
    } else if (ev.type === 'conversation.item.input_audio_transcription.completed' && ev.transcript?.trim()) {
      this.pending.push({ role: 'user', text: ev.transcript.trim() });
      this.cb.onCaption('user', ev.transcript.trim());
    } else if ((ev.type === 'response.output_audio_transcript.done' || ev.type === 'response.audio_transcript.done') && ev.transcript?.trim()) {
      this.pending.push({ role: 'agent', text: ev.transcript.trim() });
      // The one-line backtest note was said: switch to the "already said" rule
      if (DISCLAIMER_SAID.test(ev.transcript)) this.swapRule('disclaimer');
      // Agent mentioned Telegram but may not call the tool: show the card anyway
      if (/telegram|टेलीग्राम/i.test(ev.transcript)) { this.showResource('telegram'); this.swapRule('telegram'); }
      this.showNamedVideos(ev.transcript);
      this.cb.onCaption('agent', ev.transcript.trim());
    } else if (ev.type === 'input_audio_buffer.speech_started') {
      this.cb.onStatus('listening');
    } else if (ev.type === 'output_audio_buffer.started' || ev.type === 'response.output_audio.delta') {
      this.cb.onStatus('speaking');
    } else if (ev.type === 'response.done') {
      this.cb.onStatus('live');
    } else if (ev.type === 'response.function_call_arguments.done') {
      this.runTool(ev);
    }
  }

  async lookupFirm(ev, args) {
    this.cb.onStatus('checking');
    let output = { found: false, note: 'Could not reach the official site right now.' };
    try {
      const res = await fetch(`${API_URL}/api/support/conversations/${this.session.conversation_id}/prop-lookup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ firm_id: args.firm_id, question: args.question || '' }),
      });
      if (res.ok) output = await res.json();
    } catch { /* fall back to not found */ }
    if (output.card_lines?.length) {
      const firstUrl = (output.source || '').match(/https?:\/\/[^\s,`]+/)?.[0] || '';
      this.cb.onResource({ id: `rules-${Date.now()}`, type: 'rules', title: output.card_title, lines: output.card_lines, value: firstUrl, asOf: output.fetched_at });
    }
    delete output.card_lines;
    this.send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: ev.call_id, output: JSON.stringify(output) } });
    // The voice model tends to summarise long results: make this one reply read the complete answer verbatim
    this.send(output.say
      ? { type: 'response.create', response: { instructions: `${READ_VERBATIM}\n\n${output.say}` } }
      : { type: 'response.create' });
  }

  runTool(ev) {
    let args = {};
    try { args = JSON.parse(ev.arguments || '{}'); } catch { /* ignore */ }
    if (ev.name === 'lookup_prop_firm_rules') { this.lookupFirm(ev, args); return; }
    let output = { ok: false };
    if (ev.name === 'share_resource') {
      const item = this.showResource(args.resource_id);
      if (item) output = { ok: true, shown: item.title };
    } else if (ev.name === 'handoff_whatsapp') {
      this.escalated = true;
      this.cb.onHandoff(args.reason);
      output = { ok: true, shown: 'WhatsApp button' };
    }
    this.send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: ev.call_id, output: JSON.stringify(output) } });
    this.send({ type: 'response.create' });
    this.flush();
  }

  // Agent said a real synced video title without calling the tool: show that card (never anything else)
  showNamedVideos(text) {
    const said = norm(text);
    this.session.resources.filter((r) => r.id.startsWith('yt-'))
      .filter((r) => [r.title, r.title.split(/\s[-—|(]\s?/)[0]].some((t) => norm(t).length > 8 && said.includes(norm(t))))
      .forEach((r) => this.showResource(r.id));
  }

  showResource(id) {
    const item = this.session.resources.find((r) => r.id === id);
    if (item) {
      this.cb.onResource(item);
      if (!this.sharedAll?.has(id)) this.shared.push(id);
      this.sharedAll = new Set([...(this.sharedAll || []), id]);
    }
    return item;
  }

  // One-time rules (risk note, Telegram invite) switch to their "already said" version
  swapRule(name) {
    const swap = this.ruleSwaps[name];
    if (!swap) return;
    delete this.ruleSwaps[name];
    this.instructions = this.instructions.replace(swap.from, swap.to);
    this.send({ type: 'session.update', session: { type: 'realtime', instructions: this.instructions } });
    // A system note in the conversation is followed more reliably than the updated instructions alone
    this.send({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text: swap.to }] } });
  }

  setMuted(muted) {
    this.stream?.getAudioTracks().forEach((t) => { t.enabled = !muted; });
  }

  async flush() {
    if (!this.pending.length && !this.escalated && !this.shared.length) return;
    const body = { transcript: this.pending.splice(0), escalated: this.escalated, shared_resources: this.shared.splice(0) };
    try {
      await fetch(`${API_URL}/api/support/conversations/${this.session.conversation_id}/events`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true,
      });
    } catch { /* retried on next flush */ }
  }

  stop(reason = 'ended') {
    if (this.stopped) return;
    this.stopped = true;
    clearInterval(this.flushTimer);
    clearTimeout(this.capTimer);
    this.flush();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.dc?.close();
    this.pc?.close();
    this.audio?.remove();
    this.cb.onStatus(reason);
  }
}
